(() => {
  'use strict';

  document.querySelectorAll('[data-product-choice]').forEach((root) => {
    if (root.dataset.choiceInitialised === 'true') return;
    root.dataset.choiceInitialised = 'true';
    const action = root.querySelector('[data-choice-continue]');
    const retry = root.querySelector('[data-choice-retry]');
    const status = root.querySelector('[data-choice-status]');
    const price = root.querySelector('[data-choice-price]');
    const fields = [...root.querySelectorAll('[data-choice-option]')];
    const profileClient = window.DreamItArtworkProfiles;
    let data;
    let sourceEntry = null;
    let profileRoute = '';
    let currentVariant = null;
    let currentResolution = null;
    let revision = 0;
    let continuing = false;

    try {
      data = JSON.parse(root.querySelector('[data-choice-data]').textContent);
      if (root.dataset.publicState !== 'personalised_available' || !/^\d+$/.test(data.productId)
        || !['upload', 'create'].includes(data.profileRoute) || !Array.isArray(data.variants)
        || data.variants.some((variant) => !/^\d+$/.test(variant.id) || !Array.isArray(variant.options)
          || typeof variant.available !== 'boolean')
        || new Set(data.variants.map((variant) => variant.id)).size !== data.variants.length) return;
    } catch (_) { return; }

    sourceEntry = window.DreamItJourneyRoutes?.sourceHints?.(window.location.search) || null;
    profileRoute = data.profileRoute;
    if (sourceEntry) {
      const requestedRoute = sourceEntry.route || profileRoute;
      if (!Array.isArray(data.creationRoutes) || data.creationRoutes.some((route) => !['upload', 'create'].includes(route))
        || !data.creationRoutes.includes(requestedRoute)) {
        status.textContent = data.messages.unavailable;
        status.dataset.state = 'error';
        return;
      }
      profileRoute = requestedRoute;
    }

    const setStatus = (message, state = '') => {
      status.textContent = message;
      status.dataset.state = state;
      delete status.dataset.errorCode;
      delete status.dataset.errorStatus;
      delete status.dataset.errorTrace;
    };

    const showImage = (id) => {
      if (![...root.querySelectorAll('[data-choice-image]')].some((image) => image.dataset.choiceImage === id)) return;
      root.querySelectorAll('[data-choice-image]').forEach((image) => { image.hidden = image.dataset.choiceImage !== id; });
      root.querySelectorAll('[data-choice-thumbnail]').forEach((button) => {
        button.setAttribute('aria-pressed', button.dataset.choiceThumbnail === id ? 'true' : 'false');
      });
    };

    const readSelection = () => {
      if (!fields.length) return data.variants.find((variant) => variant.id === data.defaultVariantId) || null;
      const options = fields.map((field) => field.querySelector('input:checked')?.value);
      if (options.some((value) => typeof value !== 'string')) return null;
      return data.variants.find((variant) => variant.options.length === options.length
        && variant.options.every((value, index) => value === options[index])) || null;
    };

    const resolveSelection = async (focusOrigin = null) => {
      const attempt = ++revision;
      currentResolution = null;
      action.disabled = true;
      action.setAttribute('aria-busy', 'false');
      retry.hidden = true;
      const variant = currentVariant;
      price.hidden = !variant;
      if (!variant) { setStatus(data.messages.choose, 'error'); return false; }
      price.textContent = variant.price;
      showImage(variant.imageId);
      if (!variant.available) { setStatus(data.messages.unavailable, 'error'); return false; }
      setStatus(data.messages.checking, 'loading');
      action.setAttribute('aria-busy', 'true');
      try {
        if (!profileClient) throw new Error();
        const endpoint = profileClient.selectEndpoint(root.dataset.artworkProfileApiEndpoint || '', root.dataset.artworkProfileApiEnvironment || 'disabled');
        const payload = await profileClient.resolveProfile(endpoint, variant.id, profileRoute, 'auto');
        if (attempt !== revision || currentVariant?.id !== variant.id) return false;
        const resolution = profileClient.validateResolution(payload.resolution);
        if (resolution.shopify_variant_id !== variant.id || resolution.route !== profileClient.normaliseRoute(profileRoute)) throw new Error();
        currentResolution = resolution;
        action.disabled = false;
        setStatus('', 'ready');
        return true;
      } catch (error) {
        if (attempt === revision) {
          currentResolution = null;
          const rateLimited = error?.code === 'RATE_LIMITED' && error?.status === 429;
          setStatus(rateLimited ? (data.messages.rateLimited || data.messages.error) : data.messages.error, 'error');
          status.dataset.errorCode = typeof error?.code === 'string' && /^[A-Z0-9_]{1,64}$/.test(error.code)
            ? error.code : 'PROFILE_CONFIRMATION_FAILED';
          if (Number.isInteger(error?.status) && error.status >= 100 && error.status <= 599) {
            status.dataset.errorStatus = String(error.status);
          }
          if (typeof error?.traceId === 'string' && /^[A-Za-z0-9_.-]{1,128}$/.test(error.traceId)) {
            status.dataset.errorTrace = error.traceId;
          }
          retry.hidden = false;
        }
        return false;
      } finally {
        if (attempt === revision) {
          action.setAttribute('aria-busy', 'false');
          if (focusOrigin && typeof status.focus === 'function') {
            const schedule = window.requestAnimationFrame || ((callback) => window.setTimeout(callback, 0));
            schedule(() => {
              // Only an explicit retry/Continue may move focus. Initial loads,
              // variant changes and history restoration retain their position.
              if (attempt !== revision || root.isConnected === false) return;
              if (document.activeElement && document.activeElement !== focusOrigin && document.activeElement !== document.body) return;
              const target = currentResolution && !continuing ? action : status;
              if (target.hidden || target.disabled || target.closest?.('[hidden], [inert], [aria-hidden="true"]')
                || (target.getClientRects && !target.getClientRects().length)) return;
              if (target === status) target.setAttribute('tabindex', '-1');
              target.focus?.({ preventScroll: true });
              target.scrollIntoView?.({ block: 'nearest', behavior: 'auto' });
            });
          }
        }
      }
    };

    fields.forEach((field) => field.addEventListener('change', () => {
      currentVariant = readSelection();
      void resolveSelection();
    }));
    root.querySelectorAll('[data-choice-thumbnail]').forEach((button) => {
      button.addEventListener('click', () => showImage(button.dataset.choiceThumbnail));
    });
    retry.addEventListener('click', () => { void resolveSelection(retry); });
    action.addEventListener('click', async () => {
      if (continuing || action.disabled || !currentResolution || !currentVariant?.available) return;
      continuing = true;
      const variantId = currentVariant.id;
      try {
        const confirmed = await resolveSelection(action);
        if (!confirmed || currentVariant?.id !== variantId || currentResolution?.shopify_variant_id !== variantId) return;
        const destination = new URL('/pages/dream-it', window.location.origin);
        destination.searchParams.set('product', data.productId);
        destination.searchParams.set('variant', variantId);
        if (sourceEntry) {
          destination.searchParams.set('entry', 'source');
          destination.searchParams.set('route', profileRoute);
        }
        const current = () => continuing && root.isConnected !== false && currentVariant?.id === variantId
          && currentResolution?.shopify_variant_id === variantId;
        const navigate = window.DreamItJourneyRoutes?.navigateJourney;
        if (typeof navigate !== 'function') {
          if (window.DIMRuntime?.enabled) throw new Error('JOURNEY_TRANSITION_UNAVAILABLE');
          if (!current()) throw new Error('JOURNEY_STALE');
          window.location.assign(destination.href);
        } else {
          await navigate({ url: destination.href, runtime: window.DIMRuntime, location: window.location,
            assign: window.location.assign.bind(window.location), isCurrent: current });
        }
      } catch (_) {
        if (root.isConnected === false || currentVariant?.id !== variantId) return;
        setStatus(data.messages.error, 'error');
        status.setAttribute('tabindex', '-1');
        status.focus?.({ preventScroll: true });
      } finally { continuing = false; }
    });

    const hints = new URLSearchParams(window.location.search).getAll('variant');
    const hintedVariant = hints.length === 1 ? data.variants.find((variant) => variant.id === hints[0]) : null;
    if (hints.length && !hintedVariant) {
      fields.forEach((field) => field.querySelectorAll('input').forEach((input) => { input.checked = false; }));
      currentVariant = null;
      setStatus(data.messages.choose, 'error');
    } else {
      if (hintedVariant) {
        fields.forEach((field, index) => field.querySelectorAll('input').forEach((input) => {
          input.checked = input.value === hintedVariant.options[index];
        }));
      }
      currentVariant = hintedVariant || readSelection();
      void resolveSelection();
    }
    window.addEventListener('pageshow', (event) => {
      if (!event.persisted) return;
      currentVariant = readSelection();
      void resolveSelection();
    });
  });
})();
